import { Component, ElementRef, Input, ViewChild, OnChanges, SimpleChanges, OnInit, OnDestroy, inject, signal, ChangeDetectionStrategy, ChangeDetectorRef, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ClientsService } from '../../../../../services/customers/clients.service';
import * as wijmoChart from '@grapecity/wijmo.chart';
import { CurrencyPipe, DatePipe, CommonModule } from '@angular/common';
import { ResumeDataService } from '../../services/resume-data.service';
import { WjChartModule } from '@grapecity/wijmo.angular2.chart';
import { WidgetsModule } from '../../../../../shared/widgets.module';

@Component({
  selector: 'app-client-perfomance-summary',
  imports: [CommonModule, WjChartModule, WidgetsModule],
  providers: [CurrencyPipe, DatePipe],
  templateUrl: './client-perfomance-summary.component.html',
  styleUrl: './client-perfomance-summary.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class ClientPerfomanceSummaryComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('pdfViewer') pdfViewer!: ElementRef;
  @ViewChild('reportContent') reportContent!: ElementRef;
  private _flexPie!: wijmoChart.FlexPie;
  @ViewChild('flexPie', { static: false }) set flexPie(content: wijmoChart.FlexPie) {
    if (content) {
      this._flexPie = content;
      this.setupModernChartStyles();
    }
  }

  private _flexPie2!: wijmoChart.FlexPie;
  @ViewChild('flexPie2', { static: false }) set flexPie2(content: wijmoChart.FlexPie) {
    if (content) {
      this._flexPie2 = content;
      this.setupModernChartStyles();
    }
  }
  @Input() client_data!: any
  public clientInfo = {
    name: '',
    address: '',
    phone: '',
    manager: '',
    email: '',
    assistant: ''
  };

  public pkiOperativesData = [
    { pki: 'Cost per Gallon', value: '$0.00' },
    { pki: 'Tank Size', value: 'N/A' },
    { pki: 'Installation Date', value: 'N/A' },
    { pki: 'First Collection Date', value: 'N/A' },
    { pki: 'Total Oil Collected', value: 0 },
    { pki: 'Total Clean Oil', value: 0 },
    { pki: 'Total Collections Count', value: 0 },
    { pki: 'Avg. Days Between Collections', value: 0 },
    { pki: 'Avg. Oil per Collection', value: 0 }
  ];

  public pkiFinancialData = [
    { pki: 'Total Debt History', value: '$0.00' },
    { pki: 'Total Paid to Client', value: '$0.00' },
    { pki: 'Current Debt', value: '$0.00' }
  ];

  public pkiBehaviorData = [
    { pki: 'Total Paid to Sales Person', value: '$0.00' },
    { pki: 'Total Paid to Referrer', value: '$0.00' },
    { pki: 'Estimated Oil (Year-end)', value: 0 },
    { pki: 'Estimated Debt (Year-end)', value: '$0.00' },
    { pki: 'Referrer Name', value: 'N/A' },
    { pki: 'Referrer Paid', value: '$0.00' }
  ];

  oilData = [
    { name: 'Clean', value: 0 },
    { name: 'Waste', value: 0 }
  ];

  paymentData = [
    { name: 'Paid', value: 0 },
    { name: 'Debt', value: 0 }
  ];

  hasOilData = signal<boolean>(false);
  hasPaymentData = signal<boolean>(false);
  totalOilFormatted = signal<string>('0 Gallons');
  totalPaidFormatted = signal<string>('$0.00');
  currentDebtFormatted = signal<string>('$0.00');

  clientSummaryData: any = {}

  readonly resumeData = inject(ResumeDataService)
  loading = signal(false)

  private activatedRoute = inject(ActivatedRoute);
  private clientsService = inject(ClientsService);
  private currencyPipe = inject(CurrencyPipe);
  private datePipe = inject(DatePipe);
  private cdr = inject(ChangeDetectorRef);
  private renderTimeout: any;

  constructor() { }

  ngOnInit() {
    const routeId = this.activatedRoute.snapshot.paramMap.get('id');
    if (routeId) {
      this.loadClientSummaryData(routeId);
    }
  }

  ngOnDestroy() {
    if (this.renderTimeout) {
      clearTimeout(this.renderTimeout);
    }
  }

  ngAfterViewInit() {
    this.setupModernChartStyles();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['client_data'] && changes['client_data'].currentValue) {
      const clientId = this.client_data?._id;
      if (clientId) {
        this.loadClientSummaryData(clientId);
      }
    }
  }

  setupModernChartStyles() {
    // Oil Chart - Modern Green & Red
    if (this._flexPie) {
      this._flexPie.palette = ['#10b981', '#ef4444']; // Clean Green, Waste Red
      this._flexPie.plotMargin = 'NaN';
      this._flexPie.innerRadius = 0.65;
      this._flexPie.selectedItemOffset = 0.05;
      this._flexPie.selectedItemPosition = wijmoChart.Position.Top;
      this._flexPie.refresh();
    }

    // Payment Chart - Modern Green & Amber
    if (this._flexPie2) {
      this._flexPie2.palette = ['#10b981', '#fb923c']; // Paid Green, Debt Amber
      this._flexPie2.plotMargin = 'NaN';
      this._flexPie2.innerRadius = 0.65;
      this._flexPie2.selectedItemOffset = 0.05;
      this._flexPie2.selectedItemPosition = wijmoChart.Position.Top;
      this._flexPie2.refresh();
    }
  }

  loadClientSummaryData(customerId: string) {
    if (!customerId) return;

    // Reset chart data to zero BEFORE the HTTP call so the MutationObserver never
    // captures stale SVG paths from the previous client during the transition.
    this.hasOilData.set(false);
    this.hasPaymentData.set(false);
    this.totalOilFormatted.set('0 Gal');
    this.totalPaidFormatted.set('$0.00');
    this.currentDebtFormatted.set('$0.00');
    this.oilData = [
      { name: 'Clean 0.0%', value: 0 },
      { name: 'Waste 0.0%', value: 0 }
    ];
    this.paymentData = [
      { name: 'Total Paid: $0.00', value: 0 },
      { name: 'Debt: $0.00', value: 0 }
    ];

    this.loading.set(true);
    this.clientsService.getClientReport(customerId)
      .subscribe({
        next: (res: any) => {
          const report = res.report || res.data || (res.total_oil_collected !== undefined ? res : null);

          if (!report) {
            this.loading.set(false);
            return;
          }

          this.clientSummaryData = report;
          this.resumeData.performanceSummaryTab.set(report);

          // Tabla 1: General & Operational Info
          this.pkiOperativesData = [
            { pki: 'Cost per Gallon', value: this.formatToUSD(report.cost_per_gallon) },
            { pki: 'Tank Size', value: report.tank_size ? `${this.formatNumber(report.tank_size, 0)} Gallons` : 'N/A' },
            { pki: 'Installation Date', value: this.dateFormat(report.installation_date) || 'N/A' },
            { pki: 'First Collection Date', value: this.dateFormat(report.first_collection_date) || 'N/A' },
            { pki: 'Last Collection Date', value: this.dateFormat(report.last_collection_date) || 'N/A' },
            { pki: 'Total Oil Collected', value: `${this.formatNumber(report.total_oil_collected)} Gallons` },
            { pki: 'Total Clean Oil', value: `${this.formatNumber(report.total_clean_oil)} Gallons` },
            { pki: 'Total Collections Count', value: this.formatNumber(report.total_collections_count, 0) },
            { pki: 'Avg. Days Between Collections', value: `${this.formatNumber(report.avg_days_between_collections, 0)} Days` },
            { pki: 'Avg. Oil per Collection', value: `${this.formatNumber(report.avg_oil_per_collection, 0)} Gallons` }
          ];

          // Tabla 2: Performance & Metrics
          this.pkiFinancialData = [
            { pki: 'Total Debt History', value: this.formatToUSD(report.total_debt_history) },
            { pki: 'Total Paid to Client', value: this.formatToUSD(report.total_paid_to_client) },
            { pki: 'Current Debt', value: this.formatToUSD(report.current_debt) }
          ];

          // Tabla 3: Predictions & Partners
          this.pkiBehaviorData = [
            { pki: 'Total Paid to Sales Person', value: this.formatToUSD(report.total_paid_to_seller) },
            { pki: 'Total Paid to Referrer', value: this.formatToUSD(report.total_paid_to_referrer) },
            { pki: 'Estimated Oil (Year-end)', value: `${this.formatNumber(report.projections?.estimated_oil_remaining_year)} Gallons` },
            { pki: 'Estimated Debt (Year-end)', value: this.formatToUSD(report.projections?.estimated_debt_remaining_year) },
            { pki: 'Referrer Name', value: report.referrer?.name || 'N/A' },
            { pki: 'Referrer Paid', value: this.formatToUSD(report.total_paid_to_referrer) }
          ];

          // Gráficos y flags de datos reales
          const rawTotalOil = Number(report.total_oil_collected) || 0;
          const rawCleanOil = Number(report.total_clean_oil) || 0;
          const rawTotalPaid = Number(report.total_paid_to_client) || 0;
          const rawCurrentDebt = Number(report.current_debt) || 0;

          this.hasOilData.set(rawTotalOil > 0);
          this.hasPaymentData.set(rawTotalPaid > 0 || rawCurrentDebt > 0);

          this.totalOilFormatted.set(`${this.formatNumber(rawTotalOil)} Gal`);
          this.totalPaidFormatted.set(this.formatToUSD(rawTotalPaid));
          this.currentDebtFormatted.set(this.formatToUSD(rawCurrentDebt));

          const total = rawTotalOil > 0 ? rawTotalOil : 1;
          const clean = rawCleanOil;
          this.oilData = [
            { name: `Clean ${((clean / total) * 100).toFixed(1)}%`, value: clean },
            { name: `Waste ${(((total - clean) / total) * 100).toFixed(1)}%`, value: total - clean }
          ];

          this.paymentData = [
            { name: `Total Paid: ${this.formatToUSD(rawTotalPaid)}`, value: rawTotalPaid },
            { name: `Debt: ${this.formatToUSD(rawCurrentDebt)}`, value: rawCurrentDebt }
          ];
          this.setupModernChartStyles();

          // Delay to ensure FlexPie charts are fully rendered before PDF pagination triggers.
          // 1500ms gives Wijmo SVG enough time to complete its internal draw cycle
          // after itemsSource changes, preventing stale SVG clones in the output container.
          this.renderTimeout = setTimeout(() => {
            this.loading.set(false);
            this.cdr.markForCheck();
          }, 1500);

        },
        error: (err) => {
          this.loading.set(false);
          this.cdr.markForCheck();
        }
      });
  }

  formatToUSD(value: number): string {
    return this.currencyPipe.transform(value, 'USD', 'symbol', '1.2-2') || '$0.00';
  }

  formatNumber(value: any, decimals: number = 2): string {
    const num = Number(value) || 0;
    return num.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  }

  dateFormat(date: string): string {
    if (!date) return '';
    return this.datePipe.transform(date, 'MM/dd/yyyy') ?? '';
  }
}
